import {
  OpportunityCard,
  OpportunityCardContent,
} from '../components/OpportunityCard';
import { BellRing } from 'lucide-react';
import { createOpportunityClientsTableColumns } from './OpportunityClientsTable';
import { DataTable } from '../components/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState, useMemo } from 'react';
import {
  EngagementSheetPrimaryBtn,
  OpportunityEngagementSheet,
} from '@/components/OpportunityEngagementSheet';
import { fillOutIntakeFormReminderTemplate } from '@shared';
import {
  useHandleEmailSentStatus,
  useOpportunityState,
} from '@/pages/AdvisorDashboardOpportunities';
import { SendOpportunityMessageModal } from './SendOpportunityMessageModal';

export function OpportunityFillOutIntakeForms() {
  const {
    clientsIsLoading,
    status,
    priorityStatus,
    sheetOpen,
    setSheetOpen,
    advisorOpportunitiesState,
    qualifyingClients,
    clientsEligibleForReminder,
  } = useOpportunityState('FILL_OUT_INTAKE_FORM');

  useHandleEmailSentStatus(
    advisorOpportunitiesState,
    () => setSheetOpen(false),
    'FILL_OUT_INTAKE_FORM',
  );

  const engagementSheet = useMemo(
    () => (
      <OpportunityIntakeFormEngagementSheet
        sheetOpen={sheetOpen}
        setSheetOpen={setSheetOpen}
        clientsWithIncompleteIntakeForms={qualifyingClients}
        clientsEligibleForReminder={clientsEligibleForReminder}
        buttonTrigger={
          <EngagementSheetPrimaryBtn
            priorityStatus={priorityStatus}
            onClick={() => setSheetOpen(true)}
            actionText={`View Clients`}
          />
        }
      />
    ),
    [
      sheetOpen,
      qualifyingClients,
      clientsEligibleForReminder,
      priorityStatus,
      setSheetOpen,
    ],
  );

  if (priorityStatus === 'Not applicable') return null;

  return (
    <OpportunityCard
      priorityStatus={priorityStatus}
      isLoading={status === 'fetchingClients' || clientsIsLoading}
      cardContent={
        <OpportunityCardContent
          icon={
            <BellRing
              className="h-8 w-8"
              strokeWidth={1}
            />
          }
          primaryMetric={qualifyingClients.length.toString()}
          metricDescription="Clients haven't submitted their intake forms yet."
          secondaryMetric={clientsEligibleForReminder.length.toString()}
          secondaryMetricDescription={`${
            clientsEligibleForReminder
              ? clientsEligibleForReminder.length === 1
                ? 'client needs a reminder'
                : 'clients need reminders'
              : '0'
          }`}
        />
      }
      cardFooterContent={engagementSheet}
      data-testid="opportunity-card-fill-out-intake-forms"
    />
  );
}

function useEmailTemplateState() {
  const [advisorProvidedBody, setAdvisorProvidedBody] = useState<
    string | undefined
  >(undefined);
  const [isEditMessageOpen, setIsEditMessageOpen] = useState(false);

  const handleUpdateMessage = (body: string) => {
    setAdvisorProvidedBody(body);
    setIsEditMessageOpen(false);
  };

  return {
    advisorProvidedBody,
    isEditMessageOpen,
    setIsEditMessageOpen,
    handleUpdateMessage,
  };
}

function OpportunityIntakeFormEngagementSheet({
  sheetOpen,
  setSheetOpen,
  clientsWithIncompleteIntakeForms,
  clientsEligibleForReminder,
  buttonTrigger,
}: {
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
  clientsWithIncompleteIntakeForms: Client[];
  clientsEligibleForReminder: Client[];
  buttonTrigger: React.ReactNode;
}) {
  const { advisor, clientsIsLoading, status } = useOpportunityState(
    'FILL_OUT_INTAKE_FORM',
  );

  const [selectedClients, setSelectedClients] = useState<Client[]>([]);

  const { advisorProvidedBody, isEditMessageOpen, setIsEditMessageOpen } =
    useEmailTemplateState();

  useEffect(() => {
    if (!sheetOpen) {
      setSelectedClients([]);
    }
  }, [sheetOpen]);

  const isLoading = status === 'sendingEmails';

  const memoizedDataTable = useMemo(
    () => (
      <DataTable<Client>
        key={clientsWithIncompleteIntakeForms.map(c => c.clientId).join('-')}
        columns={
          createOpportunityClientsTableColumns(
            'FILL_OUT_INTAKE_FORM',
          ) as ColumnDef<Client>[]
        }
        getRowId={row => row.clientId}
        data={clientsWithIncompleteIntakeForms}
        isLoading={status === 'fetchingClients' || clientsIsLoading}
        className="mb-12"
        tableId="fillOutIntakeFormTable"
        variant="outlined"
        tableSkeletonRows={10}
        defaultSelection={clientsEligibleForReminder.filter(
          client => client.clientEmail,
        )}
        pageSize={8}
        hideColumnSelector={true}
        onSelectionChange={setSelectedClients}
        isRowDeactivated={row => !row.clientEmail}
      />
    ),
    [
      clientsWithIncompleteIntakeForms,
      status,
      clientsIsLoading,
      clientsEligibleForReminder,
    ],
  );

  return (
    <>
      <OpportunityEngagementSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        trigger={buttonTrigger}
        title="Clients with incomplete intake forms"
        description="These are clients who received an invitation to fill out their intake forms but haven't submitted them yet. Auto-selected for next steps."
        clientsTable={memoizedDataTable}
        floatingMenuCtaProps={{
          text: `Review and send reminder`,
          onClick: () => setIsEditMessageOpen(true),
          disabled: isLoading,
        }}
        selectedClientCount={selectedClients.length}
      />

      <SendOpportunityMessageModal
        isOpen={isEditMessageOpen}
        onClose={() => setIsEditMessageOpen(false)}
        advisor={advisor}
        defaultMessage={advisorProvidedBody}
        emailTemplateProps={{
          clientName: '{{ Client Name }}',
          intakeFormUrl: '#',
          advisorFirstName: advisor.advisorFirstName,
          advisorLastName: advisor.advisorLastName,
          advisorEmail: advisor.advisorEmail,
          advisorOrganization: advisor.organizationDisplayName ?? '',
          emailPreferencesLink: '',
          isPreview: true,
        }}
        emailTemplate={fillOutIntakeFormReminderTemplate}
        selectedClients={selectedClients}
        engagementType="FILL_OUT_INTAKE_FORM"
      />
    </>
  );
}
